import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";

/**
 * Hook to automatically track page views when the route changes
 * This should be used once in the main App component
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    // Get the page title from the document
    const pageTitle = document.title;

    // Track the page view
    trackPageView(location.pathname + location.search, pageTitle);
  }, [location]);
}

/**
 * Hook to track custom events in components
 * Returns tracking functions for common analytics events
 */
export function useAnalytics() {
  const location = useLocation();

  return {
    /**
     * Track when a user views an agent
     */
    trackAgentView: (agentId: string, agentName: string) => {
      trackPageView(`/agent/${agentId}`, `Agent: ${agentName}`);
    },

    /**
     * Track when a user searches
     */
    trackSearch: (searchTerm: string, resultsCount?: number) => {
      // Import the trackSearch function from analytics
      import("@/lib/analytics").then(({ trackSearch }) => {
        trackSearch(searchTerm, resultsCount);
      });
    },

    /**
     * Track when a user submits an agent
     */
    trackAgentSubmission: (success: boolean, agentName?: string) => {
      import("@/lib/analytics").then(({ trackEvent }) => {
        trackEvent("agent_submission", {
          event_category: "agent",
          event_label: agentName,
          success,
          page_path: location.pathname,
        });
      });
    },

    /**
     * Track navigation clicks
     */
    trackNavigation: (linkText: string, destination: string) => {
      import("@/lib/analytics").then(({ trackEvent }) => {
        trackEvent("navigation_click", {
          event_category: "navigation",
          event_label: linkText,
          destination,
          page_path: location.pathname,
        });
      });
    },

    /**
     * Track button clicks
     */
    trackButtonClick: (buttonText: string, buttonType?: string) => {
      import("@/lib/analytics").then(({ trackEvent }) => {
        trackEvent("button_click", {
          event_category: "interaction",
          event_label: buttonText,
          button_type: buttonType,
          page_path: location.pathname,
        });
      });
    },
  };
}
